import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { LogOut, Settings, Camera } from 'lucide-react';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

// ── Fit text to a fixed-height container ──────────────────────────────────────
function useFitText(text, maxSize = 20, minSize = 10) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(maxSize);

  useEffect(() => {
    const container = containerRef.current;
    const el = textRef.current;
    if (!container || !el) return;
    let size = maxSize;
    el.style.fontSize = size + 'px';
    while (el.scrollHeight > container.clientHeight && size > minSize) {
      size -= 0.5;
      el.style.fontSize = size + 'px';
    }
    setFontSize(size);
  }, [text, maxSize, minSize]);

  return { containerRef, textRef, fontSize };
}

// ── App colour tokens ─────────────────────────────────────────────────────────
const APP_COLORS = {
  mirror:    '#6B35B8',
  uncode:    '#E85D24',
  giraffe:   '#10B981',
  framed:    '#D4537E',
  cultofone: '#F5A623',
  cult:      '#F5A623',
  dash:      '#06B6D4',
  soulsync:  '#4B5FD4',
  zephyr:    '#6B35B8',
  soul:      '#AFA9EC',
  matrix:    '#f0f0f5',
  arx:       '#C0A96A',
  both:      '#E8C547',
  verve:     '#FF0042',
  flux:      '#E24B4A',
};

const zar = (n) => `R${Math.round(n || 0).toLocaleString('en-ZA')}`;

const DAY_NAMES   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getTaskTagLabel(task) {
  if (task.tag)        return task.tag.toUpperCase();
  if (task.source_app) return task.source_app.toUpperCase();
  if (task.domain)     return task.domain.toUpperCase();
  return null;
}

// ── Right rail section — scales on hover ──────────────────────────────────────
function RailSection({ onClick, children, style = {} }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transform: hovered ? 'scale(1.03)' : 'scale(1)',
        transformOrigin: 'left center',
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Task row ──────────────────────────────────────────────────────────────────
function TaskRow({ task, isLast, onNavigate }) {
  const [hovered, setHovered] = useState(false);
  const tagLabel = getTaskTagLabel(task);
  const taskId   = task._id || task.id;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate('/uncode', { state: { focusTaskId: taskId } })}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 16,
        padding: '13px 0',
        cursor: 'pointer',
        borderBottom: isLast ? 'none' : '0.5px solid rgba(255,255,255,0.05)',
      }}
    >
      <span style={{
        flex: 1,
        fontFamily: "'Inter', sans-serif",
        fontSize: hovered ? 15.5 : 13.5,
        fontWeight: 300,
        color: APP_COLORS.uncode,
        transition: 'font-size 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        lineHeight: 1.45,
      }}>
        {task.title || task.body || '—'}
      </span>
      {tagLabel && (
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9,
          letterSpacing: '0.16em',
          color: APP_COLORS.uncode,
          opacity: hovered ? 0 : 0.5,
          transition: 'opacity 0.2s ease',
          flexShrink: 0,
          textTransform: 'uppercase',
        }}>
          {tagLabel}
        </span>
      )}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ value, color }) {
  return (
    <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', borderRadius: 1, overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: `${Math.min(100, Math.max(0, value))}%`,
        background: color,
        borderRadius: 1,
        transition: 'width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }} />
    </div>
  );
}

// ── Rail divider ──────────────────────────────────────────────────────────────
function RailDivider() {
  return <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.06)', margin: '20px 0' }} />;
}

// ── Mono label ────────────────────────────────────────────────────────────────
function RailLabel({ children, color }) {
  return (
    <p style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 9,
      color: color || 'rgba(154,154,154,0.55)',
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      marginBottom: 7,
    }}>
      {children}
    </p>
  );
}

// ── App section card (desktop grid) ───────────────────────────────────────────
function AppSection({ color, logo, onClick, children, style = {} }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: `3px solid ${color}55`,
        background: hovered ? `${color}1A` : `${color}0D`,
        borderRadius: 4,
        padding: '18px 22px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.3s ease',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      {children}
      {logo && (
        <img
          src={logo}
          alt=""
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 28,
            height: 28,
            borderRadius: 7,
            opacity: 0.7,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

// ── Photo card ────────────────────────────────────────────────────────────────
function PhotoCard({ photo, onClick, style = {} }) {
  const [hovered, setHovered] = useState(false);
  if (!photo?.url) return <div style={{ borderRadius: 4, background: 'rgba(255,255,255,0.02)', ...style }} />;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        borderRadius: 4,
        overflow: 'hidden',
        backgroundImage: `url(${photo.url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: hovered ? 1 : 0.22,
        transition: 'opacity 0.5s ease',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        ...style,
      }}
    >
      <img src="/logo-framed.png" alt="" style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: 7, opacity: 0.7, pointerEvents: 'none' }} />
    </div>
  );
}

// ── Mobile layout component (hook must live at component level) ───────────────
function MobileLayout({ currentVoice, activeTrip, navigate, APP_COLORS, logout }) {
  const quoteText = currentVoice ? `\u201c${currentVoice.text}\u201d` : '';
  const { containerRef, textRef, fontSize } = useFitText(quoteText, 20, 10);

  const apps = [
    { path: '/cult',     logo: '/logo-cult.png',     color: APP_COLORS.cultofone, pre: 'C',   rest: 'ULT',     tagline: 'Manifest of a Movement'  },
    { path: '/framed',   logo: '/logo-framed.png',   color: APP_COLORS.framed,    pre: 'F',   rest: 'RAMED',   tagline: 'A Life in Technicolor'    },
    { path: '/flux',     logo: '/logo-flux.png',     color: APP_COLORS.flux,      pre: 'F',   rest: 'LUX'                                          },
    { path: '/soulsync', logo: '/logo-soulsync.png', color: APP_COLORS.soulsync,  pre: 'S',   rest: 'OULSYNC', tagline: 'Architecting The Self'    },
    { path: '/verve',    logo: '/logo-verve.png',    color: APP_COLORS.verve,     isVerve: true,               tagline: 'Forging Freedom'          },
    { path: '/uncode',   logo: '/logo-uncode.png',   color: APP_COLORS.uncode,    pre: 'U',   rest: 'NCODE',   tagline: 'Chaos into Clarity'       },
    { path: activeTrip ? `/giraffe/${activeTrip.id}` : '/giraffe',
                         logo: '/logo-giraffe.png',  color: APP_COLORS.giraffe,   pre: 'G',   rest: 'IRAFFE',  tagline: 'The Journey Unraveled'    },
  ];

  const utilItems = [
    { label: 'Gallery', icon: <Camera size={16} strokeWidth={1.5} />, action: () => window.open(`${window.location.protocol}//${window.location.hostname}/gallery`, '_blank') },
    { label: 'Sync', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 8a5.5 5.5 0 1 1-1.1-3.3"/><polyline points="11 2 13.5 4.5 11 7"/></svg>, action: () => window.dispatchEvent(new CustomEvent('zephyr-sync-claude')) },
    { label: 'Admin', icon: <Settings size={16} strokeWidth={1.5} />, action: () => navigate('/admin/architecture') },
    { label: 'Logout', icon: <LogOut size={16} strokeWidth={1.5} />, action: () => { logout(); navigate('/auth'); } },
  ];

  return (
    <div className="flex flex-col md:hidden" style={{ position: 'relative', zIndex: 2, height: '100vh', overflow: 'hidden', padding: '0 0 80px' }}>

      {/* Cult of One voice — fixed height, text auto-fits */}
      {currentVoice && (
        <div style={{ flexShrink: 0, height: '19vh', padding: '12px 24px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
          <div ref={containerRef} style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <p ref={textRef} style={{
              fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic',
              fontSize, lineHeight: 1.45, color: APP_COLORS.cultofone,
            }}>
              {quoteText}
            </p>
          </div>
          <p style={{
            flexShrink: 0, marginTop: 6, fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, letterSpacing: '0.15em', color: 'rgba(240,240,245,0.28)',
          }}>
            — {currentVoice.author}
          </p>
        </div>
      )}

      {/* App grid — 2 columns */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {apps.map((app, i) => (
          <div key={i}>
            <button
              onClick={() => navigate(app.path)}
              style={{
                width: '100%', height: 88,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '10px 8px', gap: 7,
                background: `${app.color}0D`,
                border: `3px solid ${app.color}`,
                borderRadius: 14,
                cursor: 'pointer',
              }}
            >
              <img src={app.logo} alt={app.isVerve ? 'Verve' : app.rest} style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0 }} />
              <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 11, fontWeight: 300, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#f0f0f5', lineHeight: 1 }}>
                {app.isVerve ? 'VERVE' : `${app.pre}${app.rest}`}
              </span>
            </button>
          </div>
        ))}

        {/* Bottom row: utility options (left) + Zephyr (right) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {utilItems.map((item, i) => (
            <button
              key={i}
              onClick={item.action}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 4, padding: '8px 4px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                cursor: 'pointer',
                color: '#b0b4c0',
              }}
            >
              {item.icon}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#b0b4c0' }}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
        <div>
          <button
            onClick={() => navigate('/zephyr')}
            style={{
              width: '100%', height: '100%', minHeight: 88,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '10px 8px', gap: 7,
              background: `${APP_COLORS.zephyr}0D`,
              border: `3px solid ${APP_COLORS.zephyr}`,
              borderRadius: 14,
              cursor: 'pointer',
            }}
          >
            <img src="/logo-zephyr.png" alt="Zephyr" style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0 }} />
            <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 11, fontWeight: 300, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#f0f0f5', lineHeight: 1 }}>
              ZEPHYR
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tasks,         setTasks]         = useState([]);
  const [activeTrip,    setActiveTrip]    = useState(null);
  const [soulsyncStats, setSoulsyncStats] = useState({ total: 0 });
  const [latestEntry,   setLatestEntry]   = useState(null);
  const [voices,        setVoices]        = useState([]);
  const [voiceIdx,      setVoiceIdx]      = useState(0);
  const [allPhotos,     setAllPhotos]     = useState([]);
  const [verveStats, setVerveStats] = useState(null);
  const [fluxSummary, setFluxSummary] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [dailyInsight, setDailyInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);
  const [insightError, setInsightError] = useState(false);
  const [insightExpanded, setInsightExpanded] = useState(false);
  const [todayEntries, setTodayEntries] = useState([]);

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!voices.length) return;
    const today = new Date();
    const seed  = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    setVoiceIdx(seed % voices.length);
  }, [voices.length]);

  const loadAll = async () => {
    // Fetch weekly pulse separately — it can be slow on first generation
    api.get('/zephyr/daily-insight').then(r => {
      setDailyInsight(r.data);
      setInsightLoading(false);
    }).catch(() => { setInsightLoading(false); setInsightError(true); });

    try {
      const results = await Promise.allSettled([
        api.get('/trips'),
        api.get('/tasks?status=open'),
        api.get('/mantras'),
        api.get('/photos?include_collection=true'),
        api.get('/soulsync/stats'),
        api.get('/soulsync/entries?limit=1'),
      ]);

      const [tripsRes, tasksRes, mantrasRes, photosRes, statsRes, entriesRes] = results;

      if (tripsRes.status === 'fulfilled') {
        const trips = tripsRes.value.data || [];
        const today = new Date().toISOString().split('T')[0];
        const active = trips.find(t => t.status === 'active' && t.end_date >= today) ||
          trips.find(t => t.start_date <= today && t.end_date >= today) || null;
        setActiveTrip(active || null);
        if (active?.id) {
          api.get(`/trips/${active.id}`).then(r => {
            const entries = r.data?.entries || [];
            setTodayEntries(entries.filter(e => e.date === today).slice(0, 6));
          }).catch(() => {});
        }
      }

      if (tasksRes.status === 'fulfilled') {
        const raw   = tasksRes.value.data || [];
        const order = { hot: 0, burning: 0, warm: 1, cold: 1 };
        setTasks([...raw]
          .filter(t => t.status !== 'solved')
          .sort((a, b) => (order[a.urgency] ?? 2) - (order[b.urgency] ?? 2))
          .slice(0, 5)
        );
      }

      if (mantrasRes.status === 'fulfilled') {
        const mantras = mantrasRes.value.data || [];
        const all     = mantras.flatMap(m =>
          (m.quotes || []).filter(q => q.text && q.author).map(q => ({
            text:   q.text,
            author: q.author,
            mantra: m.title || m.name || '',
          }))
        );
        setVoices(all);
      }

      if (photosRes.status === 'fulfilled') {
        setAllPhotos((photosRes.value.data || []).slice(0, 20));
      }

      if (statsRes.status === 'fulfilled')   setSoulsyncStats(statsRes.value.data);
      if (entriesRes.status === 'fulfilled') {
        const e = entriesRes.value.data || [];
        if (e.length) setLatestEntry(e[0]);
      }

      // Verve stats
      api.get('/verve/stats').then(r => setVerveStats(r.data)).catch(() => {});
      api.get('/flux/summary?months=4').then(r => setFluxSummary(r.data)).catch(() => {});

    } finally {
      setLoading(false);
    }
  };

  // Derived values
  const now           = new Date();
  const tripLocation  = activeTrip?.destination || null;
  const dateline      = [
    DAY_NAMES[now.getDay()],
    `${now.getDate()} ${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`,
    tripLocation,
  ].filter(Boolean).join(' · ');

  const tripDayNum    = activeTrip ? Math.max(1, Math.floor((now - new Date(activeTrip.start_date)) / 86400000) + 1) : 0;
  const tripTotalDays = activeTrip ? Math.max(1, Math.round((new Date(activeTrip.end_date) - new Date(activeTrip.start_date)) / 86400000) + 1) : 0;
  const tripProgress  = tripTotalDays > 0 ? (tripDayNum / tripTotalDays) * 100 : 0;

  const currentVoice  = voices.length > 0 ? voices[voiceIdx % voices.length] : null;
  const bgUrl         = allPhotos[0]?.url;

  // Daily-seeded card photo — pick 1 from the pool (skipping index 0 which is bg)
  const daySeed    = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  const cardPool   = allPhotos.slice(1);
  const cardPhoto1 = cardPool.length > 0 ? cardPool[daySeed % cardPool.length] : null;

  return (
    <div data-testid="dashboard-page" style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>


      {/* ── Background ─────────────────────────────────────────────────────── */}
      {bgUrl ? (
        <>
          <div style={{
            position: 'fixed', inset: 0, zIndex: 0,
            backgroundImage: `url(${bgUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(8px) brightness(0.22)',
            transform: 'scale(1.06)',
          }} />
          <div style={{ position: 'fixed', inset: 0, zIndex: 1, background: 'rgba(13,13,13,0.62)' }} />
        </>
      ) : (
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: '#0a0a0f' }} />
      )}

      {/* ── Mobile layout ────────────────────────────────────────────────── */}
      <MobileLayout
        currentVoice={currentVoice}
        activeTrip={activeTrip}
        navigate={navigate}
        APP_COLORS={APP_COLORS}
        logout={logout}
      />

      {/* ── Desktop layout ────────────────────────────────────────────────── */}
      <div className="hidden md:flex" style={{ position: 'relative', zIndex: 2, flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

        {/* ─── Row 1: Zephyr insight ────────────────────────────────────── */}
        <div style={{ flexShrink: 0, padding: '16px 40px 0', display: 'flex', gap: 10, maxWidth: 1060 + 80, margin: '0 auto', boxSizing: 'border-box' }}>
          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setInsightExpanded(v => !v)}>
            <div style={{
              border: `3px solid ${APP_COLORS.zephyr}55`,
              background: insightExpanded ? `${APP_COLORS.zephyr}18` : `${APP_COLORS.zephyr}0D`,
              borderRadius: 4,
              padding: '22px 28px',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
            }}>
              {insightExpanded && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <button
                    onClick={e => { e.stopPropagation(); navigate('/zephyr', { state: { mode: 'ask', entryTitle: 'Weekly Pulse', entryBody: [dailyInsight?.summary, dailyInsight?.expansion].filter(Boolean).join('\n\n'), sourceApp: 'zephyr-dashboard' } }); }}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#fff', background: `${APP_COLORS.zephyr}55`, border: `1px solid ${APP_COLORS.zephyr}88`, borderRadius: 3, padding: '5px 10px', cursor: 'pointer' }}
                  >
                    Discuss with Zephyr
                  </button>
                </div>
              )}
              {insightLoading ? (
                <ZLoadingBar accent="#6B35B8" loading={true} />
              ) : dailyInsight ? (
                <>
                  <p style={{
                    fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic',
                    fontSize: 18, lineHeight: 1.55, color: '#FFFFFF',
                  }}>
                    {dailyInsight.summary}
                  </p>
                  {insightExpanded && dailyInsight.expansion && (
                    <div style={{ marginTop: 18, borderTop: `1px solid ${APP_COLORS.zephyr}33`, paddingTop: 16 }}>
                      {dailyInsight.expansion.split('\n\n').map((para, i) => (
                        <p key={i} style={{
                          fontFamily: "'Inter', sans-serif", fontWeight: 300,
                          fontSize: 13, lineHeight: 1.7, color: 'rgba(255,255,255,0.75)',
                          marginBottom: i === 0 ? 12 : 0,
                        }}>
                          {para}
                        </p>
                      ))}
                    </div>
                  )}
                </>
              ) : insightError ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.12em' }}>
                    Could not generate insight
                  </p>
                  <button
                    onClick={() => {
                      setInsightError(false);
                      setInsightLoading(true);
                      api.get('/zephyr/daily-insight').then(r => {
                        setDailyInsight(r.data);
                        setInsightLoading(false);
                      }).catch(() => { setInsightLoading(false); setInsightError(true); });
                    }}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', background: 'none', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}
                  >
                    retry
                  </button>
                </div>
              ) : (
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.12em' }}>
                  No pulse this week
                </p>
              )}
              <p style={{
                marginTop: 10,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.25)',
              }}>
                {dateline}
              </p>
              <img src="/logo-zephyr.png" alt="" style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: 7, opacity: 0.7, pointerEvents: 'none' }} />
            </div>
          </div>

        </div>

        {/* ─── App grid — 4-column dense, cards resize by data ─────────── */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '12px 40px 20px' }}>
          <div style={{
            maxWidth: 1060, margin: '0 auto',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridTemplateRows: '1fr 1fr 1fr',
            gridAutoFlow: 'row dense',
            gap: 10,
          }}>

            {/* Giraffe — span 2 with active trip, span 1 without */}
            <AppSection
              color={APP_COLORS.giraffe} logo="/logo-giraffe.png"
              onClick={() => navigate(activeTrip ? `/giraffe/${activeTrip.id}` : '/giraffe')}
              style={{ gridColumn: `span ${activeTrip ? 2 : 1}` }}
            >
              {activeTrip ? (
                <>
                  <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 16, color: '#FFFFFF', lineHeight: 1.3, marginBottom: 6 }}>
                    {activeTrip.title}
                  </p>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
                    {activeTrip.destination ? <span style={{ color: 'rgba(255,255,255,0.38)' }}>{activeTrip.destination} · </span> : null}
                    <span style={{ color: APP_COLORS.giraffe }}>Day {tripDayNum} of {tripTotalDays}</span>
                  </p>
                  <ProgressBar value={tripProgress} color={APP_COLORS.giraffe} />
                  {todayEntries.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {todayEntries.slice(0, 5).map(e => {
                        const STATUS_DOT = { confirmed: '#10B981', urgent: '#e63946', planning: '#f4a261' };
                        const dot = STATUS_DOT[e.status];
                        const isHotel = e.logistics_type === 'hotel';
                        const isLogistics = !!e.logistics_type;
                        return (
                          <div key={e.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: dot, flexShrink: 0 }} />}
                              {e.time && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: APP_COLORS.giraffe, flexShrink: 0 }}>{e.time}</span>}
                              {isLogistics && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>{e.logistics_type}</span>}
                              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 300, color: 'rgba(255,255,255,0.75)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</span>
                            </div>
                            {e.location && (
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.25)', paddingLeft: isLogistics || e.time ? 11 : 0 }}>{e.location}</span>
                            )}
                            {isHotel && e.end_date && (
                              <div style={{ display: 'flex', gap: 6, paddingLeft: 11, fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}>
                                <span style={{ color: APP_COLORS.giraffe }}>IN</span>
                                <span style={{ color: 'rgba(255,255,255,0.35)' }}>{e.date}</span>
                                <span style={{ color: 'rgba(255,255,255,0.2)' }}>→</span>
                                <span style={{ color: APP_COLORS.giraffe }}>OUT</span>
                                <span style={{ color: 'rgba(255,255,255,0.35)' }}>{e.end_date}</span>
                              </div>
                            )}
                            {e.confirmation && (
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(255,255,255,0.2)', paddingLeft: 11 }}>{e.confirmation}</span>
                            )}
                          </div>
                        );
                      })}
                      {todayEntries.length > 5 && (
                        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>+{todayEntries.length - 5} more today</p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                !loading && <ZEmptyState mark="Z" accent="#6B35B8" message="No active trip" />
              )}
            </AppSection>

            <PhotoCard photo={cardPhoto1} onClick={() => navigate('/framed')} />

            {/* Uncode */}
            <AppSection color={APP_COLORS.uncode} logo="/logo-uncode.png" onClick={() => navigate('/uncode')}>
              {tasks.length > 0 ? tasks.slice(0, 4).map((task, i) => (
                <p key={task._id || task.id} style={{
                  fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300,
                  color: '#FFFFFF', lineHeight: 1.45,
                  marginBottom: i < tasks.slice(0, 4).length - 1 ? 8 : 0,
                  opacity: i === 0 ? 1 : 0.55,
                }}>
                  {task.title || task.body || '—'}
                </p>
              )) : (
                !loading && <ZEmptyState mark="Z" accent="#6B35B8" message="No open tasks" />
              )}
            </AppSection>

            {/* SoulSync */}
            <AppSection color={APP_COLORS.soulsync} logo="/logo-soulsync.png" onClick={() => navigate('/soulsync')}>
              {latestEntry ? (
                <>
                  <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, color: '#FFFFFF', fontStyle: 'italic', lineHeight: 1.5, flex: 1 }}>
                    {latestEntry.title || (latestEntry.body || '').substring(0, 200)}
                  </p>
                  {latestEntry.body && latestEntry.title && (
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 300, color: 'rgba(255,255,255,0.45)', lineHeight: 1.55, marginTop: 10 }}>
                      {(latestEntry.body || '').substring(0, 160)}
                    </p>
                  )}
                </>
              ) : (
                !loading && <ZEmptyState mark="Z" accent="#6B35B8" message="No entries yet" />
              )}
            </AppSection>

            {/* Verve */}
            <AppSection color={APP_COLORS.verve} logo="/logo-verve.png" onClick={() => navigate('/verve')} style={{ padding: '14px 16px', gridColumn: `span ${activeTrip ? 1 : 2}` }}>
              {verveStats ? (
                <>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, color: verveStats.today?.net_kj < 0 ? '#10B981' : APP_COLORS.verve, lineHeight: 1, marginBottom: 6 }}>
                    {verveStats.today?.net_kj < 0 ? '−' : '+'}{Math.abs(Math.round(verveStats.today?.net_kj || 0)).toLocaleString()} kJ
                  </p>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em' }}>PROTEIN</span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: APP_COLORS.verve }}>{Math.round(verveStats.today?.protein_g || 0)}g</span>
                    </div>
                    <div style={{ height: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 1, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(100, ((verveStats.today?.protein_g || 0) / 170) * 100)}%`, background: APP_COLORS.verve, borderRadius: 1 }} />
                    </div>
                  </div>
                  {verveStats.progress?.current_actual_weight_kg && (
                    <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', marginBottom: 3 }}>
                      {verveStats.progress.current_actual_weight_kg} kg → {verveStats.progress.target_weight_kg || 83} kg
                    </p>
                  )}
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'capitalize' }}>
                    {(verveStats.progress?.pullups_current_phase || 'dead_hang').replace('_', ' ')} · {verveStats.progress?.pullups_latest_reps || 0} reps
                  </p>
                  {verveStats.body_composition?.measured_date && (
                    <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(255,255,255,0.22)', marginTop: 4 }}>weighed {verveStats.body_composition.measured_date}</p>
                  )}
                </>
              ) : (
                !loading && <ZEmptyState mark="Z" accent="#6B35B8" message="No data yet" />
              )}
            </AppSection>

            {/* Flux: latest imported month vs the 3 before it */}
            {(() => {
              const months = fluxSummary?.months || [];
              const latest = months[months.length - 1];
              const prior = months.slice(0, -1);
              const avg = prior.length ? prior.reduce((a, m) => a + (m.total || 0), 0) / prior.length : null;
              const delta = latest && avg ? ((latest.total - avg) / avg) * 100 : null;
              const label = latest ? new Date(`${latest.month}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }) : '';
              return (
                <AppSection color={APP_COLORS.flux} logo="/logo-flux.png" onClick={() => navigate('/flux')} style={{ padding: '14px 16px', gridColumn: 'span 2' }}>
                  {latest ? (
                    <>
                      <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</p>
                      <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, color: APP_COLORS.flux, lineHeight: 1, marginBottom: 8 }}>{zar(latest.total)}</p>
                      {delta !== null && (
                        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: delta > 0 ? APP_COLORS.flux : '#10B981', letterSpacing: '0.08em', marginBottom: 3 }}>
                          {delta > 0 ? '+' : '−'}{Math.abs(Math.round(delta))}% vs {prior.length}-mo avg {zar(avg)}
                        </p>
                      )}
                      {latest.kids > 0 && (
                        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em' }}>kids {zar(latest.kids)}</p>
                      )}
                    </>
                  ) : (
                    !loading && <ZEmptyState mark="Z" accent="#6B35B8" message="No spend imported yet" />
                  )}
                </AppSection>
              );
            })()}

            {/* Cult of One — always full width */}
            <AppSection
              color={APP_COLORS.cultofone} logo="/logo-cult.png"
              onClick={() => navigate('/cult')}
              style={{ gridColumn: 'span 4' }}
            >
              {currentVoice ? (
                <>
                  {currentVoice.mantra && (
                    <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic', fontWeight: 300, fontSize: 36, letterSpacing: '0.04em', color: 'rgba(255,255,255,0.55)', lineHeight: 1, flex: 1 }}>
                      {currentVoice.mantra}
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexShrink: 0 }}>
                    <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontSize: 16, lineHeight: 1.55, color: '#FFFFFF', flex: 1 }}>
                      &ldquo;{currentVoice.text}&rdquo;
                    </p>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.32)', flexShrink: 0 }}>
                      — {currentVoice.author}
                    </span>
                  </div>
                </>
              ) : (
                !loading && <ZEmptyState mark="Z" accent="#6B35B8" message="No quotes loaded" />
              )}
            </AppSection>

          </div>
        </div>

      </div>
    </div>
  );
}
